// @ts-nocheck
import {
  Box,
  Card as MantineCard,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text as MantineText,
  Title,
} from '@mantine/core';
import { IconLanguage, IconWorld } from '@tabler/icons-react';
import { Card, LanguageSwitcher, Text } from '../../../components/ui';
import { SUPPORTED_LANGUAGES, usePreferencesStore } from '../../../stores';

// Sample translations for demonstration
const translations: Record<string, Record<string, string>> = {
  en: {
    welcome: 'Welcome',
    greeting: 'Hello, how are you today?',
    dashboard: 'Dashboard',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Log Out',
    save: 'Save Changes',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search...',
    notifications: 'Notifications',
    help: 'Help & Support',
  },
  es: {
    welcome: 'Bienvenido',
    greeting: '¡Hola! ¿Cómo estás hoy?',
    dashboard: 'Panel de Control',
    settings: 'Configuración',
    profile: 'Perfil',
    logout: 'Cerrar Sesión',
    save: 'Guardar Cambios',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    search: 'Buscar...',
    notifications: 'Notificaciones',
    help: 'Ayuda y Soporte',
  },
  fr: {
    welcome: 'Bienvenue',
    greeting: 'Bonjour, comment allez-vous ?',
    dashboard: 'Tableau de Bord',
    settings: 'Paramètres',
    profile: 'Profil',
    logout: 'Déconnexion',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    search: 'Rechercher...',
    notifications: 'Notifications',
    help: 'Aide et Support',
  },
  de: {
    welcome: 'Willkommen',
    greeting: 'Hallo, wie geht es Ihnen heute?',
    dashboard: 'Dashboard',
    settings: 'Einstellungen',
    profile: 'Profil',
    logout: 'Abmelden',
    save: 'Änderungen Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    search: 'Suchen...',
    notifications: 'Benachrichtigungen',
    help: 'Hilfe & Support',
  },
  pt: {
    welcome: 'Bem-vindo',
    greeting: 'Olá, como você está hoje?',
    dashboard: 'Painel',
    settings: 'Configurações',
    profile: 'Perfil',
    logout: 'Sair',
    save: 'Salvar Alterações',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    search: 'Pesquisar...',
    notifications: 'Notificações',
    help: 'Ajuda e Suporte',
  },
  zh: {
    welcome: '欢迎',
    greeting: '你好，今天怎么样？',
    dashboard: '仪表板',
    settings: '设置',
    profile: '个人资料',
    logout: '退出登录',
    save: '保存更改',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    search: '搜索...',
    notifications: '通知',
    help: '帮助与支持',
  },
  ja: {
    welcome: 'ようこそ',
    greeting: 'こんにちは、今日はいかがですか？',
    dashboard: 'ダッシュボード',
    settings: '設定',
    profile: 'プロフィール',
    logout: 'ログアウト',
    save: '変更を保存',
    cancel: 'キャンセル',
    delete: '削除',
    edit: '編集',
    search: '検索...',
    notifications: '通知',
    help: 'ヘルプ＆サポート',
  },
  ko: {
    welcome: '환영합니다',
    greeting: '안녕하세요, 오늘 기분이 어떠세요?',
    dashboard: '대시보드',
    settings: '설정',
    profile: '프로필',
    logout: '로그아웃',
    save: '변경 사항 저장',
    cancel: '취소',
    delete: '삭제',
    edit: '편집',
    search: '검색...',
    notifications: '알림',
    help: '도움말 및 지원',
  },
};

export default function TranslationsPage() {
  const language = usePreferencesStore((state) => state.language);
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language);
  const t = translations[language] || translations.en;

  return (
    <Stack gap="xl">
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Translations
        </Text>
        <MantineText c="dimmed" size="sm">
          Multi-language support with {SUPPORTED_LANGUAGES.length} languages
        </MantineText>
      </Box>

      {/* Language Selector */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="lg">
          <Group justify="space-between">
            <Group gap="sm">
              <IconLanguage size={24} />
              <Box>
                <MantineText fw={600}>Current Language</MantineText>
                <MantineText size="sm" c="dimmed">
                  {currentLang?.flag} {currentLang?.label} ({currentLang?.native})
                </MantineText>
              </Box>
            </Group>
          </Group>

          <Title order={4}>Language Switcher Variants</Title>

          <Box>
            <MantineText size="sm" fw={500} mb="sm">
              Select Dropdown
            </MantineText>
            <LanguageSwitcher variant="select" size="md" />
          </Box>

          <Box>
            <MantineText size="sm" fw={500} mb="sm">
              Menu Dropdown
            </MantineText>
            <LanguageSwitcher variant="menu" size="md" />
          </Box>

          <Box>
            <MantineText size="sm" fw={500} mb="sm">
              Segmented Control (first 4 languages)
            </MantineText>
            <LanguageSwitcher variant="segmented" size="md" />
          </Box>
        </Stack>
      </MantineCard>

      {/* Supported Languages */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Supported Languages</Title>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <Paper
                key={lang.code}
                p="sm"
                radius="md"
                withBorder
                style={{
                  borderColor: lang.code === language ? 'var(--mantine-primary-color-filled)' : undefined,
                  borderWidth: lang.code === language ? 2 : 1,
                }}
              >
                <Group gap="xs">
                  <MantineText size="xl">{lang.flag}</MantineText>
                  <Box>
                    <MantineText size="sm" fw={500}>
                      {lang.label}
                    </MantineText>
                    <MantineText size="xs" c="dimmed">
                      {lang.native}
                    </MantineText>
                  </Box>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Stack>
      </MantineCard>

      {/* Translation Preview */}
      <Card variant="gradient" gradientVariant="primary">
        <Stack gap="md">
          <Group gap="sm">
            <IconWorld size={24} />
            <MantineText fw={600} size="lg">
              {t.welcome}!
            </MantineText>
          </Group>
          <MantineText opacity={0.9}>{t.greeting}</MantineText>
        </Stack>
      </Card>

      {/* Common UI Translations */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Common UI Elements</Title>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {Object.entries(t).map(([key, value]) => (
              <Paper key={key} p="sm" radius="sm" withBorder>
                <MantineText size="xs" c="dimmed" tt="uppercase">
                  {key}
                </MantineText>
                <MantineText fw={500}>{value}</MantineText>
              </Paper>
            ))}
          </SimpleGrid>
        </Stack>
      </MantineCard>

      {/* Language Comparison */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Language Comparison: "Welcome"</Title>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {Object.entries(translations).map(([code, trans]) => {
              const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
              return (
                <Paper
                  key={code}
                  p="sm"
                  radius="md"
                  style={{
                    background:
                      code === language
                        ? 'var(--mantine-primary-color-light)'
                        : undefined,
                  }}
                >
                  <MantineText size="lg" ta="center">
                    {lang?.flag}
                  </MantineText>
                  <MantineText size="sm" ta="center" fw={500}>
                    {trans.welcome}
                  </MantineText>
                  <MantineText size="xs" c="dimmed" ta="center">
                    {lang?.label}
                  </MantineText>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
      </MantineCard>
    </Stack>
  );
}
